import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FaTasks } from '@react-icons/all-files/fa/FaTasks';
import { FaShieldAlt } from '@react-icons/all-files/fa/FaShieldAlt';
import { FaCog } from '@react-icons/all-files/fa/FaCog';
import { FaHome } from '@react-icons/all-files/fa/FaHome';
import { FaPlus } from '@react-icons/all-files/fa/FaPlus';
import Breadcrumb from '../../../commons/Breadcrumb';
import PageHeader from '../../../commons/PageHeader';
import PageAlert, { usePageAlert } from '../../../commons/PageAlert';
import Confirm, { useConfirm } from '../../../commons/Confirm';
import { EditButton, DeleteButton, RenderActions } from '../../../commons/DataGridActions';
import ProfaseDataGrid from '../../../commons/Datagrid';
import { useAppDispatch, useAppSelector } from '../../../store';
import { deleteEntity, getEntities, reset } from '../../../manager/sys-task.reducer';

const BASE = '/configuracion/seguridad/sys-tasks';

const fmtDate = (v: unknown) => {
  if (v == null || v === '') return '—';
  const d = new Date(String(v));
  return Number.isNaN(d.getTime()) ? String(v) : d.toLocaleString('es-MX', { dateStyle: 'short', timeStyle: 'short' });
};

export default function SysTasksPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const topRef = useRef<HTMLDivElement>(null);

  const entities = useAppSelector(s => s.sysTask.entities);
  const totalItems = useAppSelector(s => s.sysTask.totalItems);
  const loading = useAppSelector(s => s.sysTask.loading);
  const updating = useAppSelector(s => s.sysTask.updating);
  const updateSuccess = useAppSelector(s => s.sysTask.updateSuccess);

  const [rows, setRows] = useState<any[]>([]);
  const [lastParams, setLastParams] = useState<any>(null);

  const { alert, showToast } = usePageAlert();
  const { confirmConfig, ask, dismiss, setConfirmLoading } = useConfirm();

  useEffect(() => {
    const msg = (location.state as any)?.successMsg;
    if (msg) {
      showToast('success', msg);
      window.history.replaceState({}, '');
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (updateSuccess) {
      dispatch(reset());
      dismiss();
      if (lastParams) dispatch(getEntities(lastParams));
      showToast('success', 'Tarea de sistema eliminada correctamente.');
    }
  }, [updateSuccess]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setConfirmLoading(updating);
  }, [updating]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setRows(entities ?? []);
  }, [entities]);

  const fetchData = (params: any) => {
    setLastParams(params);
    dispatch(getEntities(params));
  };

  const renderActions = (params: any) => (
    <RenderActions>
      <EditButton onClick={() => navigate(`${BASE}/${params.row.id}/edit`)} />
      <DeleteButton
        onClick={() => {
          ask(
            <>
              ¿Confirmas que deseas eliminar la tarea <strong>{params.row.task}</strong>? Esta acción no se puede deshacer.
            </>,
            () => dispatch(deleteEntity(params.row.id)),
            { title: 'Eliminar tarea de sistema', confirmLabel: 'Eliminar', variant: 'danger' },
          );
          topRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }}
      />
    </RenderActions>
  );

  const cols = [
    { field: 'id', headerName: 'ID', flex: 0.45 },
    { field: 'task', headerName: 'Tarea', flex: 1.6 },
    { field: 'modulo', headerName: 'Módulo', flex: 1 },
    {
      field: 'created',
      headerName: 'Creado',
      flex: 1.05,
      renderCell: (p: any) => fmtDate(p.value),
    },
    { field: 'createdBy', headerName: 'Creado por', flex: 0.75 },
    {
      field: 'updated',
      headerName: 'Actualizado',
      flex: 1.05,
      renderCell: (p: any) => fmtDate(p.value),
    },
    { field: 'updatedBy', headerName: 'Actualizado por', flex: 0.85 },
    { field: '__actions', headerName: '', flex: 0.85, renderCell: renderActions },
  ];

  const filters = [
    { label: 'ID', name: 'id' },
    { label: 'Tarea', name: 'task' },
    { label: 'Módulo', name: 'modulo' },
  ];

  return (
    <div style={g.page} ref={topRef}>
      <Breadcrumb
        items={[
          { label: 'Inicio', to: '/', icon: <FaHome /> },
          { label: 'Configuración', to: '/configuracion', icon: <FaCog /> },
          { label: 'Seguridad', to: '/configuracion/seguridad', icon: <FaShieldAlt /> },
          { label: 'Tareas de Sistema', icon: <FaTasks /> },
        ]}
      />

      <PageHeader
        icon={<FaTasks />}
        iconColor="#0d9488"
        title="Tareas de Sistema"
        subtitle="Tareas y permisos agrupados por módulo"
        action={{
          label: (
            <>
              <FaPlus style={{ marginRight: 6 }} />
              Nueva tarea
            </>
          ),
          onClick: () => navigate(`${BASE}/new`),
          color: '#0d9488',
        }}
      />

      <Confirm config={confirmConfig} />
      {alert && <PageAlert alert={alert} />}

      <ProfaseDataGrid
        cols={cols}
        title=""
        filters={filters}
        fetch={fetchData}
        rowCount={totalItems}
        loading={loading}
        rows={rows}
      />
    </div>
  );
}

const g: Record<string, React.CSSProperties> = {
  page: { display: 'flex', flexDirection: 'column', gap: 24 },
};
